import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@mantine/core';
import { Breadcrumbs } from '@components';
import { QuietAddTrigger, RefetchButton } from '@shared/ui';
import { PickupPointCard, PickupPointModal, usePickupPoints } from '@modules/pickup-points';
import { SELLER_ME_QUERY_KEY, sellerOnboardingApi } from '@modules/seller-onboarding/api';
import { isSellerOwner, sellerHasSection } from '@modules/seller-staff';
import '@shared/styles/page.css';
import './ContactsPage.css';

export default function ContactsPage() {
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { data: pickupPoints = [], isLoading, error, refetch, isFetching } = usePickupPoints();
  const { data: sellerApp } = useQuery({
    queryKey: SELLER_ME_QUERY_KEY,
    queryFn: () => sellerOnboardingApi.getMe(),
  });
  const canCreatePickupPoint = isSellerOwner(sellerApp);
  const canManageContacts = sellerHasSection(sellerApp, 'CONTACTS');

  const getContent = () => {
    if (isLoading) {
      return (
        <div className="content-wrapper">
          <div className="contacts-page-loader">
            <Loader />
          </div>
        </div>
      );
    }

    if (error && refetch) {
      return (
        <div className="content-wrapper">
          <RefetchButton
            refetch={refetch}
            isLoading={isLoading || isFetching}
          />
        </div>
      );
    }

    if (pickupPoints.length === 0) {
      return (
        <div className="content-wrapper">
          <div className="contacts-page-empty">
            <p className="contacts-page-empty__title">Нет точек самовывоза</p>
            <p className="contacts-page-empty__text">
              Добавьте первую точку — покупатели смогут забирать и заказывать доставку у вас.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="content-wrapper">
        <div className="pickup-points-grid">
          {pickupPoints.map((pickupPoint) => (
            <PickupPointCard key={pickupPoint.id} pickupPoint={pickupPoint} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="page-container contacts-page">
      <Breadcrumbs items={[
        { label: 'Главная', path: '/' },
        { label: 'Контакты' }
      ]} />

      <div className="page-header">
        <div className="page-header-top">
          <div className="contacts-page-title-row">
            <h1>Точки самовывоза</h1>
            {canCreatePickupPoint && canManageContacts ? (
              <QuietAddTrigger
                onClick={() => setIsCreateModalOpen(true)}
                aria-label="Добавить точку самовывоза"
              >
                Добавить точку
              </QuietAddTrigger>
            ) : null}
          </div>
        </div>
      </div>

      {getContent()}

      {canCreatePickupPoint ? (
        <PickupPointModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          pickupPoint={null}
          onSuccess={(created) => {
            setIsCreateModalOpen(false);
            if (created?.id != null) {
              navigate(`/contacts/pickup-points/${created.id}`, {
                state: { showNewPickupHints: true },
              });
            }
          }}
        />
      ) : null}
    </div>
  );
}
